// Offline copies are private browser storage, not a backup or an authenticated session.
// The worker owns immutable app/PDF bytes, cached per account and show; IndexedDB owns the
// acknowledged snapshot and the queue. Readiness is reported only after both are complete.
import { signal } from "@preact/signals";

export const offline = signal<{ kind: "preparing" | "ready" | "unavailable"; message: string }>({
  kind: "preparing",
  message: "Preparing this app and the complete script for offline use…",
});

interface Identity {
  accountId: string;
  showId: string;
  sourceSha256: string;
}

/** Actual script transport, independent of connection guesses and offline-copy readiness. */
export const usingOffline = signal(false);

let watchedPdf: Identity | undefined;
let watchingPdfSource = false;

/** Start watching before PDF.js requests this account/show/version of the script. */
export function watchPdfSource(id: Identity, offlineStart = false): void {
  watchedPdf = { ...id };
  usingOffline.value = offlineStart;
  // Offline start has already read and validated this account/show's committed snapshot.
  if (offlineStart) snapshotKey = `${id.accountId}/${id.showId}/${id.sourceSha256}`;
  if (watchingPdfSource || !("serviceWorker" in navigator)) return;
  watchingPdfSource = true;
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    // Only the worker controlling this page can report the response it actually served.
    if (event.origin !== location.origin || !event.source || event.source !== navigator.serviceWorker.controller) return;
    const data = event.data;
    const current = watchedPdf;
    if (!current || !data || data.type !== "pdf-source" || typeof data.offline !== "boolean") return;
    if (data.accountId !== current.accountId || data.showId !== current.showId || data.sha256 !== current.sourceSha256) return;
    usingOffline.value = data.offline;
    if (data.offline) {
      // A fallback response comes only from a complete, identity-checked PDF cache entry.
      preparedKey = `${current.accountId}/${current.showId}/${current.sourceSha256}`;
      ready();
    }
  });
}

let preparedKey = "";
let preparingKey = "";
let snapshotKey = "";
let preparation: Promise<void> | undefined;

/** The one URL the PDF is fetched and cached under: account, show and hash are all in it. */
export function pdfUrl(id: Identity): string {
  return `/api/shows/${id.showId}/pdf?u=${encodeURIComponent(id.accountId)}&sha=${id.sourceSha256}`;
}

export function snapshotUnavailable(error: unknown): void {
  snapshotKey = "";
  offline.value = { kind: "unavailable", message: `Offline reopening isn't ready: this device couldn't store the show (${(error as Error).message}).` };
}

function ready(): void {
  if (preparedKey && preparedKey === snapshotKey) {
    offline.value = { kind: "ready", message: "Ready for offline use. This app and the complete script are stored on this device." };
  }
}

async function workerRequest(worker: ServiceWorker, message: Record<string, unknown>): Promise<void> {
  const { promise, resolve, reject } = Promise.withResolvers<void>();
  const channel = new MessageChannel();
  const timer = setTimeout(() => reject(new Error("Offline preparation timed out. Reconnect and try again.")), 120_000);
  channel.port1.onmessage = (event: MessageEvent<{ ok: boolean; error?: string }>) => {
    if (event.data.ok) resolve();
    else reject(new Error(event.data.error ?? "The script isn't stored on this device."));
  };
  worker.postMessage(message, [channel.port2]);
  try {
    await promise;
  } finally {
    clearTimeout(timer);
    channel.port1.close();
  }
}

async function activeWorker(): Promise<ServiceWorker> {
  if (!isSecureContext || !("serviceWorker" in navigator)) throw new Error("Offline reopening requires HTTPS (or localhost).");
  await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
  const { promise, resolve, reject } = Promise.withResolvers<ServiceWorkerRegistration>();
  const timer = setTimeout(() => reject(new Error("The offline app hasn't finished installing. Reconnect and try again.")), 120_000);
  void navigator.serviceWorker.ready.then(resolve, reject);
  let registration: ServiceWorkerRegistration;
  try {
    registration = await promise;
  } finally {
    clearTimeout(timer);
  }
  const worker = navigator.serviceWorker.controller ?? registration.active;
  if (!worker) throw new Error("The offline app hasn't activated yet.");
  return worker;
}

/** Called only after the acknowledged snapshot has committed to IndexedDB. */
export function prepareOffline(id: Identity): Promise<void> {
  const key = `${id.accountId}/${id.showId}/${id.sourceSha256}`;
  snapshotKey = key;
  if (preparedKey === key) {
    ready();
    return Promise.resolve();
  }
  if (preparingKey === key && preparation) return preparation;
  preparingKey = key;
  offline.value = { kind: "preparing", message: "Preparing this app and the complete script for offline use…" };
  preparation = (async () => {
    try {
      if (!id.sourceSha256) throw new Error("The show has no readable script.");
      // Request persistent storage where supported. A refusal doesn't prevent caching, but the
      // browser can still evict it; the UI never promises that this is a backup.
      void navigator.storage?.persist?.().catch(() => false);
      const worker = await activeWorker();
      const message = { accountId: id.accountId, showId: id.showId, sha256: id.sourceSha256 };
      try {
        await workerRequest(worker, { type: "check-pdf", ...message });
      } catch {
        await workerRequest(worker, { type: "prepare-pdf", ...message });
      }
      if (preparingKey !== key) return;
      preparedKey = key;
      ready();
    } catch (error) {
      if (preparingKey === key) offline.value = { kind: "unavailable", message: `Offline reopening isn't ready: ${(error as Error).message}` };
    } finally {
      if (preparingKey === key) preparingKey = "";
    }
  })();
  return preparation;
}

/** Remove this account's cached PDFs (all shows, or one). App files are not private and stay. */
export async function clearPdfCaches(accountId: string, showId?: string): Promise<void> {
  if (!("caches" in globalThis)) return;
  const prefix = showId ? `q2q-pdf.${accountId}.${showId}` : `q2q-pdf.${accountId}.`;
  for (const name of await caches.keys()) if (name === prefix || name.startsWith(prefix)) await caches.delete(name);
  preparedKey = "";
}
